// components/website/Footer.js - Simplified Cherry Theme
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Flower, Heart, Mail, Phone, MapPin, Send, Clock, Truck, Shield } from 'lucide-react';
import { toast } from 'react-toastify';

const Footer = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubscribing(true);
    setTimeout(() => {
      toast.success('Subscribed successfully!');
      setEmail('');
      setSubscribing(false);
    }, 1000);
  };

  return (
    <footer style={{
      background: 'linear-gradient(135deg, #1a0a0a 0%, #2d1818 50%, #1a0a0a 100%)',
      color: '#f0e6e6',
      marginTop: 'auto',
      position: 'relative',
      overflow: 'hidden',
      width: '100%'
    }}>
      {/* Decorative cherry blossom line */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: 'linear-gradient(90deg, #dc2626, #ff6b6b, #dc2626)'
      }} />

      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: isMobile ? '30px 20px 20px' : '50px 40px 30px'
      }}>
        
        {/* Main Footer Grid - Only Brand and Contact */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '30px' : '60px',
          marginBottom: isMobile ? '20px' : '30px'
        }}>
          
          {/* Brand Column */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                width: '45px',
                height: '45px',
                background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(220,38,38,0.3)'
              }}>
                <Flower size={24} color="white" />
              </div>
              <div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: 'white', margin: 0, letterSpacing: '1px' }}>Sugandhi</h3>
                <p style={{ fontSize: '0.7rem', color: '#dc2626', margin: 0 }}>Premium Florist</p>
              </div>
            </div>
            <p style={{ 
              fontSize: '0.85rem', 
              lineHeight: 1.6, 
              color: '#cbd5e1', 
              marginBottom: '20px',
              maxWidth: '400px'
            }}>
              Bringing joy and beauty to every occasion with our handcrafted floral arrangements. 
              Fresh flowers, same-day delivery across Solapur.
            </p>
            
            {/* Social Icons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                style={socialIconStyle}
                onMouseEnter={e => e.currentTarget.style.background = '#dc2626'} 
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                </svg>
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                style={socialIconStyle}
                onMouseEnter={e => e.currentTarget.style.background = '#dc2626'} 
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke="white" strokeWidth="2" fill="none"/>
                  <circle cx="12" cy="12" r="5" stroke="white" strokeWidth="2" fill="none"/>
                  <circle cx="17" cy="7" r="1" fill="white"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Contact & Newsletter Column */}
         
        </div>

        {/* Features Bar */}
        

        {/* Copyright */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          fontSize: '0.7rem',
          color: '#94a3b8',
          paddingBottom: isMobile ? '0' : '0'
        }}>
          <center>
          <div>
            © 2026 Sugandhi Flowers. All rights reserved. <br/> | Made with <Heart size={10} color="#dc2626" style={{ display: 'inline', verticalAlign: 'middle' }} /> in Solapur
          </div>
          </center>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.6rem' }}>Secure Payments:</span>
            <img src="https://razorpay.com/assets/razorpay-glyph.svg" alt="Razorpay" style={{ height: '16px', background: 'white', padding: '2px 6px', borderRadius: '4px' }} />
           
          </div>
        </div>
      </div>

      <style>{`
        .hover-link:hover {
          color: #dc2626 !important;
          transform: translateX(5px);
        }
      `}</style>
    </footer>
  );
};

const headingStyle = {
  fontSize: '1rem',
  fontWeight: 700,
  color: 'white',
  marginBottom: '20px',
  position: 'relative',
  display: 'inline-block',
  paddingBottom: '8px',
  borderBottom: '2px solid #dc2626'
};

const socialIconStyle = {
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  background: 'rgba(255,255,255,0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s',
  cursor: 'pointer',
  textDecoration: 'none'
};

export default Footer;